import { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate } from "react-router-dom"
import { ArrowLeft, Download } from "lucide-react"
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts"
import { Button } from "@/clientWeb/components/ui/button"
import { Badge } from "@/clientWeb/components/ui/badge"
import { NoData } from "@/clientWeb/components/ui/no-data"
import nodataAnalyticsImg from "@/assets/nodataanalytics.svg"

interface CountryRow {
  country: string
  code: string
  flag: string
  participants: number
  meetings: number
  pct: number
}

// No analytics API yet (see services/analytics.repo.ts), so no locations are
// known. These used to be invented figures for ten countries and six regions.
const COUNTRY_DATA: CountryRow[] = []
const REGION_DATA: { region: string; value: number }[] = []

const COLORS = ["#6366f1", "#8b5cf6", "#06b6d4", "#10b981", "#f59e0b", "#ef4444"]

export default function WorldMapPage() {
  const t = useTranslation()
  const navigate = useNavigate()
  const [activeRegion, setActiveRegion] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<"participants" | "meetings">("participants")

  const sorted = [...COUNTRY_DATA].sort((a, b) => b[sortBy] - a[sortBy])
  const total = COUNTRY_DATA.reduce((s, c) => s + c.participants, 0)
  const top = [...COUNTRY_DATA].sort((a, b) => b.participants - a.participants)[0]
  const noData = <NoData image={nodataAnalyticsImg} title={t("analytics.noDataAvailable")} size="sm" showActionIcon={false} />

  return (
    <div className="flex flex-col flex-1 h-full overflow-y-auto p-4 sm:p-6 gap-5 bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20 dark:from-slate-900 dark:via-slate-800/50 dark:to-slate-900">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" className="rounded-full h-9 w-9 shrink-0" onClick={() => navigate("/analytics")}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("analytics.geographicDistribution")}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("analytics.participantLocations")}</p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Download className="w-4 h-4" /> {t("common.download")}
        </Button>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: t("analytics.totalParticipants"), value: total.toLocaleString(), iconBg: "bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20" },
          { label: t("analytics.countries"), value: COUNTRY_DATA.length, iconBg: "bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-900/20 dark:to-green-900/20" },
          { label: t("analytics.regions"), value: REGION_DATA.length, iconBg: "bg-gradient-to-br from-purple-50 to-violet-100 dark:from-purple-900/20 dark:to-violet-900/20" },
          { label: t("analytics.topCountry"), value: top ? `${top.country} ${top.flag}` : "—", iconBg: "bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20" },
        ].map(s => (
          <div key={s.label} className="group relative bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl px-4 py-4 shadow-xs overflow-hidden text-left">
            <div className={`absolute inset-0 ${s.iconBg}`}>
              <svg className="absolute bottom-0 right-0 w-full h-full" viewBox="0 0 200 120" fill="none" preserveAspectRatio="none">
                <path d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z" fill="currentColor" className="text-white/30 dark:text-white/10" />
              </svg>
            </div>
            <div className="relative z-10">
              <div className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white mb-1">{s.value}</div>
              <div className="text-sm font-medium text-slate-600 dark:text-slate-400">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        {/* Region chart */}
        <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-5 shadow-xs">
          <h3 className="text-sm font-semibold text-foreground mb-4">By Region</h3>
          <div className="h-52">
            {REGION_DATA.length === 0 ? noData : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={REGION_DATA} layout="vertical" barSize={14}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border)" />
                <XAxis type="number" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="region" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={88} />
                <Tooltip
                  contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, fontSize: 12 }}
                  cursor={{ fill: "var(--muted)" }}
                />
                <Bar dataKey="value" radius={[0, 6, 6, 0]} name="Participants">
                  {REGION_DATA.map((_, i) => (
                    <Cell
                      key={i}
                      fill={activeRegion === REGION_DATA[i].region ? COLORS[i] : `${COLORS[i]}99`}
                      onClick={() => setActiveRegion(activeRegion === REGION_DATA[i].region ? null : REGION_DATA[i].region)}
                      style={{ cursor: "pointer" }}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Country list */}
        <div className="lg:col-span-3 bg-card border border-border rounded-2xl shadow-xs overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
            <span className="text-sm font-semibold text-foreground flex-1">Top Countries</span>
            <div className="flex gap-1.5">
              {(["participants", "meetings"] as const).map(k => (
                <button
                  key={k}
                  onClick={() => setSortBy(k)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors capitalize ${
                    sortBy === k ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>

          <div className="divide-y divide-border/50">
            {sorted.length === 0 && noData}
            {sorted.map((c, i) => (
              <div key={c.code} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors">
                <span className="text-xs text-muted-foreground font-mono w-5 shrink-0">{i + 1}</span>
                <span className="text-xl shrink-0">{c.flag}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-foreground truncate">{c.country}</p>
                    <span className="text-xs font-semibold text-foreground ml-2 shrink-0">
                      {sortBy === "participants" ? c.participants.toLocaleString() : c.meetings}
                    </span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${c.pct}%`, background: `linear-gradient(to right, #6366f1, #8b5cf6)` }}
                    />
                  </div>
                </div>
                <Badge variant="secondary" className="text-[10px] shrink-0">{c.pct}%</Badge>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
